import React, { useEffect, useMemo, useState } from 'react';
import { FiMoon, FiSun, FiX } from 'react-icons/fi';
import { useNdTheme } from '../utils/ndTheme';
import { getApiBaseURL } from '../utils/apiUrl';
import '../styles/newDesign.css';

const api = (path) => `${getApiBaseURL()}${path.startsWith('/') ? '' : '/'}${path}`;

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function spark(seed, count) {
  const out = [];
  let x = Math.abs(seed || 17) + 1;
  for (let i = 0; i < count; i += 1) {
    x = (x * 16807) % 2147483647;
    out.push(15 + (x % 86));
  }
  return out;
}

function authHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchJson(url) {
  const res = await fetch(url, { headers: authHeaders() });
  return res.json();
}

const CARDS = [
  {
    id: 'launch',
    color: 'var(--nd-blue)',
    icon: '🚀',
    title: 'Отчет запусков приложения',
    sub: 'кто, что и когда запускал на ПК',
    unit: 'сегодня',
  },
  {
    id: 'remote',
    color: 'var(--nd-violet)',
    icon: '🏠',
    title: 'Отчет Удаленка',
    sub: 'работа вне офиса · дни и часы',
    unit: 'сотрудников',
  },
  {
    id: 'local',
    color: 'var(--nd-green)',
    icon: '🖥',
    title: 'Отчет активности локальных ПК',
    sub: 'первый вход · выход · переработки',
    unit: 'ПК в сети',
  },
  {
    id: 'onec',
    color: 'var(--nd-red)',
    icon: '🧾',
    title: 'История 1С',
    sub: 'сеансы, базы, действия в 1С',
    unit: 'события',
    badge: '1С',
  },
];

export default function WorkTimeHub({ onClose, isMobile = false, onOpenLaunch, onOpenRemote, onOpenLocal, onOpenOneC }) {
  const { isLight: ndIsLight, toggle: toggleNdTheme } = useNdTheme();
  const today = todayIso();
  const [stats, setStats] = useState({ launch: 0, remote: 0, local: 0, onec: 0 });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const [launchData, remoteData, localData, onecData] = await Promise.all([
        fetchJson(api(`/api/app-usage/report?start=${today}&end=${today}`)).catch(() => ({})),
        fetchJson(api(`/api/remote-worktime-report?start=${today}&end=${today}`)).catch(() => ({})),
        fetchJson(api(`/api/local-worktime-report?start=${today}&end=${today}`)).catch(() => ({})),
        fetchJson(api(`/api/onec-history-report?start=${today}&end=${today}`)).catch(() => ({})),
      ]);
      if (cancelled) return;

      const launchReport = Array.isArray(launchData.report) ? launchData.report : [];
      const remoteReport = Array.isArray(remoteData.report) ? remoteData.report : [];
      const localReport = Array.isArray(localData.report) ? localData.report : [];
      const onecReport = Array.isArray(onecData.report) ? onecData.report : [];

      const launchSessions = launchReport.reduce((sum, row) => sum + (Array.isArray(row.sessions) ? row.sessions.length : (row.first_start ? 1 : 0)), 0);
      const onecEvents = onecReport.reduce((sum, row) => {
        const n = Number(row.documentsCount ?? row.docsCount ?? row.events ?? row.count ?? 0);
        return sum + (n > 0 ? n : 1);
      }, 0);

      setStats({
        launch: launchSessions || launchReport.filter(r => r.first_start).length,
        remote: remoteReport.length,
        local: localReport.length,
        onec: onecEvents || onecReport.length,
      });
    };

    load();
    return () => { cancelled = true; };
  }, [today]);

  const openers = useMemo(() => ({
    launch: onOpenLaunch,
    remote: onOpenRemote,
    local: onOpenLocal,
    onec: onOpenOneC,
  }), [onOpenLaunch, onOpenRemote, onOpenLocal, onOpenOneC]);

  return (
    <div className={`nd-monWrap${isMobile ? ' nd-mobile' : ''}`}>
      <div className="nd-monSheet">
        <div className="nd-sheetHead">
          <div className="nd-sheetIco">⏱</div>
          <h2>
            Мониторинг отработанного времени
            <span>отчеты по запускам · удаленке · активности ПК · 1С</span>
          </h2>
          <button
            type="button"
            className="nd-themeBtn"
            onClick={toggleNdTheme}
            title={ndIsLight ? 'Включить тёмную тему' : 'Включить светлую тему'}
          >
            {ndIsLight ? <FiMoon /> : <FiSun />}
          </button>
          {onClose && (
            <button type="button" className="nd-sheetClose" onClick={onClose} aria-label="Закрыть" title="Закрыть">
              <FiX />
            </button>
          )}
        </div>

        <div className="nd-statChips">
          <span className="nd-sChip" style={{ '--sc': 'var(--nd-blue)' }}><i />Запусков сегодня <b>{stats.launch}</b></span>
          <span className="nd-sChip" style={{ '--sc': 'var(--nd-violet)' }}><i />Удаленно <b>{stats.remote}</b></span>
          <span className="nd-sChip" style={{ '--sc': 'var(--nd-green)' }}><i />ПК онлайн <b>{stats.local}</b></span>
          <span className="nd-sChip" style={{ '--sc': 'var(--nd-red)' }}><i />Событий 1С <b>{stats.onec}</b></span>
        </div>

        <div className="nd-cardsGrid">
          {CARDS.map((item, idx) => (
            <button
              key={item.id}
              type="button"
              className="nd-repCard nd-glass"
              style={{ '--sc': item.color, animationDelay: `${idx * 70}ms` }}
              onClick={() => openers[item.id]?.()}
            >
              {item.badge && <span className="nd-repBadge">{item.badge}</span>}
              <div className="nd-repIco">{item.icon}</div>
              <div className="nd-repT">
                <b>{item.title}</b>
                <span>{item.sub}</span>
                <div className="nd-bars">
                  {spark(stats[item.id] + idx * 11, 14).map((h, i) => (
                    <i key={i} style={{ height: `${h}%`, animationDelay: `${i * 40}ms` }} />
                  ))}
                </div>
              </div>
              <div className="nd-repRight">
                <span className="nd-repStat">{stats[item.id]}<em>{item.unit}</em></span>
                <span className="nd-repArrow">→</span>
              </div>
            </button>
          ))}
        </div>

        <div className="nd-footRow">
          {onClose && (
            <button type="button" className="nd-closeBig" onClick={onClose}>✕ Закрыть</button>
          )}
          <span className="nd-footHint">Выберите отчет — сразу откроется полный отчёт</span>
        </div>
      </div>
    </div>
  );
}
